import { isInStock } from "@babackapp/return-widget-components/lib/variant-availability.lib";
import { usePublicProductRecommendations } from "@babackapp/private-api-client/client/use-public-product-recommendations";
import type { PublicProductRecommendation } from "@babackapp/private-api-client/client/use-public-product-recommendations";
import { useExchangeSuggestions } from "@babackapp/private-api-client/client/use-exchange-suggestions";
import type { ExchangeReason } from "@babackapp/private-api-client/client/use-exchange-suggestions";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useReturnReasons } from "@babackapp/return-widget-components/hooks/use-return-reasons";
import { useReturnableItems } from "@babackapp/return-widget-components/hooks/use-returnable-items";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useWidgetParams } from "./use-widget-params";

export type SuggestionItem = {
  id: string;
  label: string;
  subtitle?: string;
  imageUrl: string | null;
  variantId?: string;
};

export type SuggestionResult = {
  items: SuggestionItem[];
  currentVariantTitle: string | null;
} & (
  | { type: "sizing"; isLoading: boolean }
  | { type: "products"; isLoading: boolean }
);

const SIZING_CATEGORIES = new Set<ExchangeReason>([
  "SIZE_TOO_LARGE",
  "SIZE_TOO_SMALL",
]);

const PRODUCT_REC_CATEGORIES = new Set(["STYLE", "UNWANTED", "OTHER"]);

type StockableVariant = {
  id: string;
  inventory_quantity: number;
  inventoryPolicy?: string;
};
type OrderWithVariants = {
  variantByLineItemId?: Record<
    string,
    { products: { productId: string; variants: StockableVariant[] }[] }
  >;
};
type ShopWithExchange = { exchange?: { minimalStock?: number } };
/** Stable fallback: `loadedVariants` is a memo dependency. */
const NO_VARIANTS: StockableVariant[] = [];

/** Same rule as use-variant-selector.ts, on the same data. */
export function isVariantInStock(
  variants: StockableVariant[],
  variantId: string,
  minimalStock: number,
): boolean {
  const variant = variants.find((v) => v.id === variantId);
  return variant ? isInStock(variant, minimalStock) : false;
}

function buildProductItems(
  recs: PublicProductRecommendation[],
): SuggestionItem[] {
  return recs.slice(0, 6).map((rec) => ({
    id: rec.targetProductId,
    label: rec.targetProductTitle ?? "",
    imageUrl: rec.targetImageUrl,
  }));
}

export function useResolutionSuggestions(): SuggestionResult | null {
  const { lineItemId = "" } = useParams<{ lineItemId: string }>();
  const { orderId, shopId, shopInternalId, shopSlug } = useWidgetParams();
  const t = useCustomText();
  const draft = useDraftReturn(orderId);
  const reasons = useReturnReasons(shopId);
  const returnableItems = useReturnableItems(orderId, shopSlug);

  const draftItem = draft?.lineItems.find((li) => li.lineItemId === lineItemId);
  const lineItem = returnableItems.find((li) => li.line_item_id === lineItemId);
  const selectedReason =
    reasons.find((r) => r.id === draftItem?.reasonId) ?? null;

  const sizingReason = SIZING_CATEGORIES.has(
    selectedReason?.standardCategory as ExchangeReason,
  )
    ? (selectedReason?.standardCategory as ExchangeReason)
    : null;

  const productId = lineItem?.product?.productId ?? null;
  const currentVariantId = lineItem?.variant?.variant_id ?? null;
  const currentVariantTitle = lineItem?.variant?.title ?? null;

  const { data: exchangeItems = [], isLoading: isLoadingSizing } =
    useExchangeSuggestions(
      shopInternalId,
      sizingReason ? productId : null,
      sizingReason ? currentVariantId : null,
      sizingReason,
    );

  const isProductRecReason = PRODUCT_REC_CATEGORIES.has(
    selectedReason?.standardCategory ?? "",
  );

  const { data: productRecs = [], isLoading: isLoadingRecs } =
    usePublicProductRecommendations(
      shopInternalId,
      isProductRecReason ? productId : null,
    );

  const { data: order } = usePublicOrder(orderId);
  const { data: shop } = usePublicShop(shopId);
  const minimalStock = (shop as ShopWithExchange)?.exchange?.minimalStock ?? 0;
  // Suggestions are sizes of the purchased product: in collection mode
  // `products[0]` can be a sibling, so the purchase is looked up by id. A
  // purchase the back left out (no eligible variant, or past the collection's
  // first 180 products) has no variant to check, so nothing is suggested.
  const loadedVariants =
    (order as OrderWithVariants | undefined)?.variantByLineItemId?.[
      lineItemId
    ]?.products?.find((p) => p.productId === productId)?.variants ??
    NO_VARIANTS;

  // The variant picker disables out-of-stock sizes, so suggesting one leads the
  // customer to a choice they cannot confirm. Its own stock comes from the
  // order's `variantByLineItemId`, live-checked against Shopify when the order
  // loaded — the endpoint's `isInStock` is read from `catalogProductVariantInventory`,
  // a table nothing in the codebase writes to, so it is always false and would
  // filter every suggestion away.
  const sizingItems = useMemo(
    () =>
      exchangeItems
        .filter((item) =>
          isVariantInStock(loadedVariants, item.variantId, minimalStock),
        )
        .slice(0, 6)
        .map((item) => ({
          id: item.variantId,
          label:
            item.direction === "above"
              ? t("suggestions.sizing.above")
              : t("suggestions.sizing.below"),
          subtitle: item.sizeLabel,
          imageUrl: item.imageUrl ?? lineItem?.image ?? null,
          variantId: item.variantId,
        })),
    [exchangeItems, loadedVariants, minimalStock, lineItem?.image, t],
  );

  const productItems = useMemo(
    () => buildProductItems(productRecs),
    [productRecs],
  );

  if (!selectedReason) return null;

  if (sizingReason) {
    return {
      type: "sizing",
      items: sizingItems,
      isLoading: isLoadingSizing,
      currentVariantTitle,
    };
  }

  if (!isProductRecReason || !productId) return null;
  return {
    type: "products",
    items: productItems,
    isLoading: isLoadingRecs,
    currentVariantTitle,
  };
}
